import { faBomb, faCog, faClockRotateLeft, faGlobe } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useState } from "react"
import { cx, getMessagePayload, config } from "../utils"

import type { Message } from "../routes/microservices-messages"
import { Link, useFetcher } from "react-router-dom"
import { CopyButton } from "./CopyButton"
import { format } from "date-fns"
import { utcToZonedTime } from "date-fns-tz"

function LocalTimeZone({ time }: { time: string }) {
  const utcDate = new Date(time)
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const localDate = utcToZonedTime(new Date(utcDate), localTimeZone)
  return <span className="font-bold">{format(localDate, "yyyy-MM-dd HH:mm:ss zzz")}</span>
}

export const MessageItem = ({ message, selected, toggleSelected }: MessageItemProps) => {
  const [showTrace, setShowTrace] = useState(false)

  const { hasFileReference, docsUrl, metadataUrl } = getMessagePayload(message.payload)
  const fetcher = useFetcher()
  const metadata = (fetcher.data as Record<string, string> | undefined) ?? null

  function fetchMetadata() {
    if (metadataUrl.length !== 0) {
      fetcher.load("/message-metadata?metadataUrl=" + metadataUrl)
    }
  }

  return (
    <div className="flex w-full flex-col items-start justify-start gap-3.5 rounded-lg border-[1px] border-accent bg-base-100 p-4">
      {(message.error ?? "")?.length > 0 && (
        <h3 className="rounded-md border border-error p-2 text-sm font-semibold text-error-content dark:border-base-content dark:text-base-content text-center w-full">
          {message.error}
        </h3>
      )}
      <div
        className={cx("grid w-full grow gap-3 text-base-content", {
          "grid-rows-[repeat(2,max-content)]": showTrace,
        })}
      >
        <div className="col-start-1 col-end-2 flex min-w-0 flex-col items-start justify-start gap-4">
          <input
            type="checkbox"
            id={message.id}
            checked={selected}
            onChange={(e) => selected !== e.target.checked && toggleSelected()}
          />
          <label htmlFor={message.id} className="sr-only">
            Select message
          </label>
        </div>
        <div className="grid col-start-1 ml-6 gap-3 -mt-8">
          <div className="col-start-1 col-end-3 grid min-w-0 gap-2.5">
            {hasFileReference && (
              <>
                <a
                  href={`${config.viewerUrl}${docsUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  title="View document"
                  className="grid grid-cols-[max-content,minmax(0,1fr)] items-center justify-start gap-3 text-left"
                >
                  <FontAwesomeIcon icon={faGlobe} />
                  <span aria-labelledby={docsUrl} className="col-start-2">
                    Weblink
                  </span>
                </a>
                <button type="button" onClick={fetchMetadata}>
                  <label
                    htmlFor={`metadata-modal-${metadataUrl}`}
                    title="Open metadata modal"
                    className={
                      hasFileReference
                        ? "grid grid-cols-[max-content,minmax(0,1fr)] items-center justify-start gap-3 text-left cursor-pointer"
                        : "hidden"
                    }
                  >
                    <FontAwesomeIcon icon={faCog} />
                    Metadata
                  </label>
                </button>
              </>
            )}
            {message.trace != null && (
              <button
                type="button"
                title="Stack trace"
                onClick={() => setShowTrace(!showTrace)}
                className="grid grid-cols-[max-content,minmax(0,1fr)] items-center justify-start gap-3 text-left"
              >
                <FontAwesomeIcon icon={faBomb} />
                <span className="col-start-2">{showTrace ? "Hide" : "Show"} Trace</span>
              </button>
            )}
            {showTrace && <CopyButton id="stack_trace" label="Copy stack trace" copiedLabel="Stack trace copied!" />}
            <Link
              to={`/microservices/history?workflowId=${message.id}`}
              title="View workflow history"
              className="grid grid-cols-[max-content,minmax(0,1fr)] items-center justify-start gap-3 text-left"
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
              <span className="col-start-2">History</span>
            </Link>
            <CopyButton id={`payload-${message.id}`} label="Copy Payload" copiedLabel="Payload Copied!" />
            <p className="text-black flex min-w-0 items-center justify-start gap-2">
              <span className="text-sm">Pty:</span>
              <span className="font-bold">{message.priority ?? 0}</span>
            </p>
            {message.firstDeath?.length !== 0 && (
              <p className="text-black flex min-w-0 items-center justify-start gap-2">
                <span className="text-sm">FD:</span>
                <LocalTimeZone time={message.firstDeath} />
              </p>
            )}
            {message.lastDeath?.length !== 0 && (
              <p className="text-black flex min-w-0 items-center justify-start gap-2">
                <span className="text-sm">LD:</span>
                <LocalTimeZone time={message.lastDeath} />
              </p>
            )}
          </div>
          <div
            id={`payload-${message.id}`}
            aria-label="Message's Payload"
            className="col-start-3 col-end-6 grid grid-flow-col grid-rows-[repeat(auto-fill,minmax(20px,1fr))] gap-x-4 gap-y-2.5"
          >
            {Object.entries(message.payload).map(([key, value]) => (
              <p key={key} className="text-black flex min-w-0 items-center justify-start gap-2">
                <span className="text-sm">{key}:</span>
                <span className="truncate font-bold">{typeof value === "object" ? JSON.stringify(value) : value}</span>
              </p>
            ))}
          </div>
          {showTrace && (
            <code className="col-start-1 col-end-6 max-h-[12.5rem] overflow-auto">
              <pre id="stack_trace" className="whitespace-normal break-words">
                {message?.trace
                  ?.trim()
                  .split("\n")
                  .map((line, index) => (
                    <p key={index}>
                      {line}
                      <br />
                    </p>
                  ))}
              </pre>
            </code>
          )}
        </div>
      </div>
      <input type="checkbox" id={`metadata-modal-${metadataUrl}`} className="modal-toggle" hidden />
      <label htmlFor={`metadata-modal-${metadataUrl}`} className="modal cursor-pointer">
        <label htmlFor="" className="modal-box w-11/12 max-w-5xl">
          {metadata !== null ? (
            <>
              <header className="flex items-center justify-between pb-5">
                <h2 className="text-lg font-bold text-center">Metadata</h2>
                <CopyButton id={`copy-metadata-${metadataUrl}`} label="Copy Metadata" copiedLabel="Metadata Copied!" />
              </header>
              <table className="overflow-x-auto scrollbar-none table w-full">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody id={`copy-metadata-${metadataUrl}`}>
                  {Object.entries(metadata).map(([key, value]) => (
                    <tr key={key}>
                      <td>{key}</td>
                      <td className="whitespace-normal break-words">{String(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <label htmlFor={`metadata-modal-${metadataUrl}`} className="modal-action">
                <span className="btn btn-sm">Close</span>
              </label>
            </>
          ) : (
            <span className="justify-center align-center flex text-xl py-5">Loading...</span>
          )}
        </label>
      </label>
    </div>
  )
}

interface MessageItemProps {
  message: Message
  selected: boolean
  toggleSelected: () => void
}
